import {useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import dynamic from 'next/dynamic';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import Loader from '@jobseeker/lotus/components/Loader';

import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {metaInfo} from '@configs';

import {Chat, InjectSearchOverlay} from '@components';

import {VersionVariants} from '@jobseeker/lotus/enums';
import {useValidateAuth} from '@jobseeker/common-core';
import {LEARNING, HOME} from '@constants';
import {
  getConfigByApp,
  getProfileSchema,
  utilServerSideDeviceDetection,
} from '@utils';
import {wrapper} from '@jobseeker/common-core/stores';
import {loadSEO, getSearchBarVariant} from '@helpers';

import type {GetServerSidePropsContext} from 'next/types';

import {
  CourseCategory,
  type AppState,
  type CourseCategoryResponse,
} from '@jobseeker/common-core/types';
import {InitiateUBA, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {courseService} from '@jobseeker/common-core/services';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import cookie from 'cookie';
import HiristLearningHomepage from '@components/Learning';
import List from '@components/Learning/List';
import Typography from '@mui/material/Typography';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppHirist = PLATFORM === 'hirist';

const LoginForm = dynamic(
  () =>
    isAppHirist
      ? import(
          '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
        )
      : Promise.reject(''),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type LearningHomepageProps = {
  metaData: object;
  seoConfig: object;
  allCategoriesCourses: CourseCategory[];
  token?: string;
};

const LearningHomepage = ({
  metaData,
  seoConfig,
  allCategoriesCourses,
  token = '',
}: LearningHomepageProps) => {
  const router = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  useValidateAuth(dispatch);

  const {searchOverlay: isSearchEnabled, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const {userId} = useSelector((state: AppState) => state.auth);

  const {
    header: {
      meta: {tabsList},
    },
  } = metaData as {
    header: {meta: {tabsList: {label: string; dataId: string; tag: string}[]}};
  };

  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    InitiateUBA('L&G Home Page');

    const timeout = setTimeout(() => {
      const sessionUserId = sessionStorage.getItem('userId') || userId || '-1';

      TrackEventUBA('lghomePageView', 'view', {
        ref: router.query.ref || '',
        sessionUsername: sessionUserId,
      });
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const isDeviceLarge = useIsLargeDevice('md');

  const handleEnquiryInLoggedOutState = () => {
    setLoginOpen(true);
  };

  const hasData = Boolean(
    allCategoriesCourses.find(
      categoryCourses => categoryCourses.data.length > 0
    )
  );

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
            {isAppHirist && token.length === 0 ? (
              <HiristLearningHomepage />
            ) : hasData ? (
              <List
                isSearchEnabled={isSearchEnabled}
                allCategoriesCourses={allCategoriesCourses}
                handleEnquiryInLoggedOutState={handleEnquiryInLoggedOutState}
                isDeviceLarge={isDeviceLarge}
                tabsList={tabsList}
              />
            ) : (
              <Typography>No courses found</Typography>
            )}

            {isAppHirist && (loginOpen || enableLoginPopup) && (
              <LoginForm
                variant={VersionVariants.VERSION_TWO}
                openDialog={loginOpen || enableLoginPopup}
                setOpenDialog={() => {
                  dispatch({
                    type: 'AppContext/set_login_popup',
                    payload: false,
                  });
                  setLoginOpen(false);
                }}
                enableSignIn={true}
                onSuccess={() => window.location.reload()}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const searchBarVariant = await getSearchBarVariant({req: context.req});
    store.dispatch({
      type: 'AppContext/set_ab_variants',
      payload: {searchBar: searchBarVariant},
    });
    const metaData = getConfigByApp(metaInfo, HOME, true, LEARNING);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: LEARNING},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobdetail/set_initial_state'});

    let token = '';

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        token = cookies?.[COOKIE_LOGIN_AUTH] ?? '';
      }
    } catch (err) {}

    const {category: allCategoriesCourses = []} =
      (await courseService.getAllCategoriesCourses()) as CourseCategoryResponse;

    const seoConfig = await loadSEO(LEARNING);

    try {
      const schema: {[key: string]: string} = await getProfileSchema();
      store.dispatch({
        type: 'User/set_profile_schema',
        payload: schema,
      });
    } catch (err) {}

    return {
      props: {
        seoConfig,
        metaData,
        allCategoriesCourses,
        token,
      },
    };
  }
);

export default LearningHomepage;
